import { useEffect, useMemo, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { useRegister } from '@lemontaigu/staff/react';
import {
  contractTypeClass,
  filterRupRows,
  flattenRup,
  fmtRupDate,
  rollingWindowStart,
  sortRupRows,
  type RupRow,
  type RupSortKey,
  type StatusFilter,
} from './logic.js';

// « Registre Unique du Personnel » regeneration (Art. L1221-13). The DB is the
// source of truth (migrations 0023–0025); this renders the legal register — one
// row per hiring, ordered by numéro d'ordre, with the DERIVED columns (Nom
// complet, Ancienneté, Présent, Présent 12 mois) recomputed here, never stored.
// Print → « Enregistrer en PDF » (landscape) is the official output, matching the
// timeshift-report pattern (CSS only, no PDF toolchain).

const X = (b: boolean) => (b ? 'X' : '');

/** Column spec: label + how to pull the cell from a flattened RUP row. */
const COLUMNS: { label: string; get: (r: RupRow) => string; num?: boolean }[] = [
  { label: 'Ordre', get: (r) => String(r.contract.hireRank ?? ''), num: true },
  { label: 'ID Paie', get: (r) => r.person.employeeId },
  { label: 'Nom complet', get: (r) => r.nomComplet },
  { label: 'Nom de naissance', get: (r) => r.person.birthName ?? '' },
  { label: 'Adresse', get: (r) => r.person.address ?? '' },
  { label: 'mail', get: (r) => r.person.email ?? '' },
  { label: 'Téléphone', get: (r) => r.person.phone ?? '' },
  { label: 'N° Sécurité Sociale', get: (r) => r.person.ssn ?? '' },
  { label: 'Nationalité', get: (r) => r.person.nationality ?? '' },
  { label: 'Naissance', get: (r) => fmtRupDate(r.person.birthDate) },
  { label: 'Sexe', get: (r) => r.person.sex ?? '' },
  { label: 'Emploi', get: (r) => r.contract.emploi ?? '' },
  { label: 'Qualification', get: (r) => r.contract.qualification ?? '' },
  { label: 'PCS-ESE', get: (r) => r.contract.pcsEse ?? '' },
  { label: 'Statut', get: (r) => r.contract.statutConventionnel ?? '' },
  { label: 'Niv.', get: (r) => r.contract.niveau ?? '' },
  { label: 'Éch.', get: (r) => r.contract.echelon ?? '' },
  { label: 'Coef', get: (r) => r.contract.coef ?? '' },
  { label: 'Brut/h', get: (r) => (r.contract.brutHoraire == null ? '' : r.contract.brutHoraire.toFixed(2)), num: true },
  { label: 'Brut/mois', get: (r) => (r.contract.brutMensuel == null ? '' : String(r.contract.brutMensuel)), num: true },
  { label: 'Entrée', get: (r) => fmtRupDate(r.contract.startDate) },
  { label: 'Sortie', get: (r) => fmtRupDate(r.contract.endDate) },
  { label: 'Type', get: (r) => r.contract.contractType ?? '' },
  { label: 'N° Contrat', get: (r) => r.contract.contractNumber ?? '' },
  { label: 'TP', get: (r) => X(r.contract.tempsPartiel) },
  { label: 'Ancienneté', get: (r) => r.anciennete },
  { label: 'Motif sortie', get: (r) => r.contract.motifSortie ?? '' },
  { label: 'Présent', get: (r) => X(r.present) },
  { label: 'Présent 12m', get: (r) => X(r.present12m) },
  { label: 'Auth. Entrée', get: (r) => fmtRupDate(r.contract.authEntree) },
  { label: 'Auth. Sortie', get: (r) => fmtRupDate(r.contract.authSortie) },
  { label: 'Doc.', get: (r) => r.contract.docType ?? '' },
  { label: 'N° doc.', get: (r) => r.contract.docNumber ?? '' },
  { label: 'Appr.', get: (r) => X(r.contract.apprenti) },
  { label: 'C.Qual.', get: (r) => X(r.contract.contratQualification) },
  { label: 'C.Adapt.', get: (r) => X(r.contract.contratAdaptation) },
];

/** Everything after « Présent » is hidden by default (the legal-detail tail). */
const PRESENT_IDX = COLUMNS.findIndex((c) => c.label === 'Présent');
const DEFAULT_HIDDEN = new Set(COLUMNS.slice(PRESENT_IDX + 1).map((c) => c.label));

export function Rup() {
  const register = useRegister();
  const [rows, setRows] = useState<RupRow[] | null>(null);
  const [err, setErr] = useState('');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(DEFAULT_HIDDEN));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<RupSortKey>('ordre');
  const asOf = useMemo(() => todayIsoParis(), []);

  const visibleColumns = COLUMNS.filter((c) => !hidden.has(c.label));
  const toggleColumn = (label: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  useEffect(() => {
    void (async () => {
      try {
        setRows(flattenRup(await register.listEmployees(), asOf));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [asOf, register]);

  const present = rows?.filter((r) => r.present).length ?? 0;
  const visibleRows = useMemo(
    () => sortRupRows(filterRupRows(rows ?? [], { status, q: search }), sort),
    [rows, status, search, sort],
  );

  return (
    <div className="rup">
      <div className="rup-toolbar no-print">
        <div>
          <strong>{rows?.length ?? '…'} inscriptions</strong> · {present} présent·e·s ·
          {rows !== null && (status !== 'all' || search) ? ` ${visibleRows.length} affiché·e·s ·` : ''}
          régénéré depuis la base (source de vérité)
        </div>
        <div className="rup-toolbar-actions">
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="all">Tous</option>
            <option value="current">En poste</option>
            <option value="past">Sortis</option>
          </select>
          <input
            type="search"
            className="rup-search"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={sort} onChange={(e) => setSort(e.target.value as RupSortKey)}>
            <option value="ordre">Trier par ordre</option>
            <option value="end">Trier par fin de contrat</option>
          </select>
          <div className="rup-columns">
            <button type="button" className="auth-btn is-ghost" onClick={() => setPickerOpen((o) => !o)}>
              Colonnes ({visibleColumns.length}/{COLUMNS.length})
            </button>
            {pickerOpen && (
              <div className="rup-columns-panel">
                <div className="rup-columns-actions">
                  <button type="button" onClick={() => setHidden(new Set())}>
                    Tout afficher
                  </button>
                  <button type="button" onClick={() => setHidden(new Set(DEFAULT_HIDDEN))}>
                    Par défaut
                  </button>
                </div>
                <div className="rup-columns-grid">
                  {COLUMNS.map((c) => (
                    <label key={c.label}>
                      <input
                        type="checkbox"
                        checked={!hidden.has(c.label)}
                        onChange={() => toggleColumn(c.label)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button type="button" className="auth-btn" onClick={() => window.print()} disabled={!rows}>
            PDF
          </button>
        </div>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      <div className="rup-doc">
        <header className="rup-head">
          <div>
            <h1>Registre Unique du Personnel</h1>
            <div className="rup-emp">
              LE MONTAIGU HOTELS ET RESORTS — 9 route de Vizos, 65120 Esquièze-Sère
            </div>
          </div>
          <div className="rup-meta">
            <div>MAJ : {fmtRupDate(asOf)}</div>
            <div>12 mois glissant depuis : {fmtRupDate(rollingWindowStart(asOf))}</div>
          </div>
        </header>

        <div className="rup-table-wrap">
          <table className="rup-table">
            <thead>
              <tr>
                {visibleColumns.map((c) => (
                  <th key={c.label} className={c.num ? 'rup-num' : ''}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr>
                  <td colSpan={visibleColumns.length} className="rup-empty">
                    Chargement…
                  </td>
                </tr>
              )}
              {rows !== null && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length} className="rup-empty">
                    Aucune inscription ne correspond.
                  </td>
                </tr>
              )}
              {visibleRows.map((r) => (
                <tr key={r.contract.id} className={r.present ? 'is-present' : ''}>
                  {visibleColumns.map((c) =>
                    c.label === 'Type' ? (
                      <td key={c.label}>
                        {c.get(r) && <span className={`contract-type-badge ${contractTypeClass(r.contract.contractType)}`}>{c.get(r)}</span>}
                      </td>
                    ) : (
                      <td key={c.label} className={c.num ? 'rup-num' : ''}>
                        {c.get(r)}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
